import { FormControlLabel, FormHelperText, Grid2 as Grid, Stack, Switch, TextField } from '@mui/material';
import { useState } from 'react';

import type { AvailableLanguage } from '../../api/business/types';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName } from '../../components/localisedText';
import { AVAILABILITY_TEXT_MAX, emptyTexts } from '../../components/productDraft';
import type { DraftSetter, DraftTextSetter, ProductDraft } from '../../components/productDraft';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductAvailabilityFieldsProps = {
  draft: ProductDraft;
  set: DraftSetter;
  setText: DraftTextSetter;
  languages: Array<string>;
  available: Array<AvailableLanguage>;
};

/**
 * Availability on the product form (R0030 WP-07): what the shop says in and out of stock, per content language;
 * the date a pre-order ships; whether it can be ordered, its price shown — hiding the price stops orders too —, sold
 * online only, with the "on sale" badge.
 */
export const ProductAvailabilityFields = ({
  draft,
  set,
  setText,
  languages,
  available,
}: ProductAvailabilityFieldsProps) => {
  const { language, t } = useLanguage();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const texts = draft.texts[current] ?? emptyTexts();
  const suffix = languages.length > 1 ? ` (${languageName(current, available, language)})` : '';

  return (
    <Stack spacing={2}>
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={current}
          onChange={setActive}
          missing={(code) => (draft.texts[code]?.availableNow ?? '').trim() === ''}
        />
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={`${t('availability.availableNow')}${suffix}`}
            helperText={t('availability.availableNowHint')}
            value={texts.availableNow}
            inputProps={{ maxLength: AVAILABILITY_TEXT_MAX }}
            onChange={(e) => setText(current, 'availableNow', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={`${t('availability.availableLater')}${suffix}`}
            helperText={t('availability.availableLaterHint')}
            value={texts.availableLater}
            inputProps={{ maxLength: AVAILABILITY_TEXT_MAX }}
            onChange={(e) => setText(current, 'availableLater', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            type="date"
            label={t('availability.availableFrom')}
            helperText={t('availability.availableFromHint')}
            value={draft.availableFrom}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => set('availableFrom', e.target.value)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={draft.showPrice}
                onChange={(e) => {
                  set('showPrice', e.target.checked);

                  // A hidden price cannot be ordered: switching it off stops orders as well.
                  if (!e.target.checked) {
                    set('availableForOrder', false);
                  }
                }}
              />
            }
            label={t('availability.showPrice')}
          />
          <FormHelperText sx={{ mt: 0, ml: 6 }}>{t('availability.showPriceHint')}</FormHelperText>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={draft.availableForOrder}
                disabled={!draft.showPrice}
                onChange={(e) => set('availableForOrder', e.target.checked)}
              />
            }
            label={t('availability.availableForOrder')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch checked={draft.onlineOnly} onChange={(e) => set('onlineOnly', e.target.checked)} />
            }
            label={t('availability.onlineOnly')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch checked={draft.onSaleBadge} onChange={(e) => set('onSaleBadge', e.target.checked)} />
            }
            label={t('availability.onSaleBadge')}
          />
          <FormHelperText sx={{ mt: 0, ml: 6 }}>{t('availability.onSaleBadgeHint')}</FormHelperText>
        </Grid>
      </Grid>
    </Stack>
  );
};
